import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, offset, shift,
  useDismiss, useFloating, useFocus, useHover, useInteractions, useRole
} from '@floating-ui/react'

const TOOLTIP_BG = '#1F2A37'

// `z-[1100]` > drawer z-1050 (cf Drawer.jsx panelStyle) : sinon le tooltip est masqué
// Par le drawer quand ouvert depuis l'intérieur (SectionDrawer, etc.).
const TOOLTIP_CLASSES = 'tw:z-[1100] tw:px-3 tw:py-1.5 tw:rounded-md! tw:w-max tw:max-w-md '
  + 'tw:text-sm tw:font-medium tw:text-white tw:text-center '
  + 'tw:shadow-sm tw:break-words'

const FOCUSABLE_PROPS = { tabIndex: 0 }
const NO_PROPS = {}
const focusProps = focusable => focusable ? FOCUSABLE_PROPS : NO_PROPS

const Tooltip = ({ children, content = null, className = '', placement = 'top', focusable = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })]
  })

  const hover = useHover(context, { delay: { open: 150, close: 0 }, move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  return (
    <>
      <span ref={refs.setReference} className={className} {...focusProps(focusable)} {...getReferenceProps()}>
        {children}
      </span>
      {isOpen && content ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={TOOLTIP_CLASSES}
            style={{ ...floatingStyles, backgroundColor: TOOLTIP_BG }}
            {...getFloatingProps()}
          >
            {content}
            <FloatingArrow ref={arrowRef} context={context} fill={TOOLTIP_BG} />
          </div>
        </FloatingPortal>
      ) : null}
    </>
  )
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  content: PropTypes.node,
  focusable: PropTypes.bool,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
}

Tooltip.defaultProps = {
  className: '',
  content: null,
  focusable: false,
  placement: 'top'
}

export default Tooltip
